import { AxiosPromise, AxiosResponse } from 'axios';

import {
  buildAuditorsQuery,
  buildCreateAuditorBody,
  buildDeactivateAuditorBody,
  buildResetPasswordBody,
  buildUpdateAuditorBody,
  normalizeFieldErrors,
  readAuditor,
  readAuditorCreated,
  readAuditorOptions,
  readAuditorPasswordReset,
  readAuditors,
} from './auditorsWire';
import { mmHttp, MmRequestConfig } from './mmHttp';
import { readPage } from './pagination';
import { MmAuditor, MmAuditorMessenger, MmAuditorPasswordReset } from '../types/domain';
import { MmAuditorCreated, MmPage } from '../types/dto';

const AUDITORS_PATH = '/auditors';

export type MmAuditorMessengerPayload = MmAuditorMessenger;

export interface MmAuditorPayload {
  login: string;
  fullName: string;
  lastName?: string | null;
  firstName?: string | null;
  middleName?: string | null;
  email: string | null;
  /** Телефон уходит как введён, без маски; пустое поле — null, а не пустая строка */
  phone: string | null;
  messengers?: MmAuditorMessengerPayload[];
  /** Регион кладём прочитанным значением: PUT заменяет запись целиком, без поля он сотрётся в базе */
  region?: string | null;
  tradingNetworkId: number;
}

/** Сервер игнорирует tradingNetworkId и берёт организацию из токена */
export function fetchAuditors(
  tradingNetworkId: number,
  query: { q?: string | null },
  pageParams: { [key: string]: any },
  config?: MmRequestConfig,
): Promise<MmPage<MmAuditor>> {
  const params = Object.assign(
    { tradingNetworkId: tradingNetworkId },
    query,
    buildAuditorsQuery(pageParams),
  );

  return mmHttp
    .get(AUDITORS_PATH, Object.assign({ params: params, mmSection: 'auditors/list' }, config))
    .then((response) => {
      const page = readPage<any>(response.data);

      return {
        content: readAuditors(page.content),
        totalElements: page.totalElements,
        totalKnown: page.totalKnown,
        hasMore: page.hasMore,
      };
    });
}

export function fetchAuditorOptions(config?: MmRequestConfig): AxiosPromise<MmAuditor[]> {
  return mmHttp
    .get(AUDITORS_PATH + '/options', Object.assign({ mmSection: 'auditors/options' }, config))
    .then((response: AxiosResponse) => {
      response.data = readAuditorOptions(response.data);

      return response;
    });
}

export function createAuditor(
  payload: MmAuditorPayload,
  config?: MmRequestConfig,
): AxiosPromise<MmAuditorCreated> {
  return mmHttp
    .post(
      AUDITORS_PATH,
      buildCreateAuditorBody(payload),
      Object.assign({ mmSection: 'auditors/create' }, config),
    )
    .then((response: AxiosResponse) => {
      response.data = readAuditorCreated(response.data);

      return response;
    })
    .catch((error: any) => {
      normalizeFieldErrors(error);

      return Promise.reject(error);
    });
}

export function updateAuditor(
  auditorId: number | string,
  payload: MmAuditorPayload,
  config?: MmRequestConfig,
): AxiosPromise<MmAuditor> {
  return mmHttp
    .put(
      AUDITORS_PATH + '/' + String(auditorId),
      buildUpdateAuditorBody(payload),
      Object.assign({ mmSection: 'auditors/update' }, config),
    )
    .then((response: AxiosResponse) => {
      response.data = readAuditor(response.data);

      return response;
    })
    .catch((error: any) => {
      normalizeFieldErrors(error);

      return Promise.reject(error);
    });
}

/* Интерфейс её не вызывает: кнопки удаления нет, судьба связок аудитора не определена */
export function deleteAuditor(auditorId: number | string, config?: MmRequestConfig): AxiosPromise<void> {
  return mmHttp.delete(
    AUDITORS_PATH + '/' + String(auditorId),
    Object.assign({ mmSection: 'auditors/delete' }, config),
  );
}

export function fetchAuditorById(
  auditorId: number | string,
  config?: MmRequestConfig,
): AxiosPromise<MmAuditor> {
  return mmHttp
    .get(
      AUDITORS_PATH + '/' + String(auditorId),
      Object.assign({ mmSection: 'auditors/get' }, config),
    )
    .then((response: AxiosResponse) => {
      response.data = readAuditor(response.data);

      return response;
    });
}

/** Без tempPassword сервер придумывает пароль сам и возвращает его в ответе */
export function resetAuditorPassword(
  auditorId: number | string,
  tempPassword?: string | null,
  config?: MmRequestConfig,
): AxiosPromise<MmAuditorPasswordReset> {
  return mmHttp
    .post(
      AUDITORS_PATH + '/' + String(auditorId) + '/reset-password',
      buildResetPasswordBody(tempPassword),
      Object.assign({ mmSection: 'auditors/resetPassword' }, config),
    )
    .then((response: AxiosResponse) => {
      response.data = readAuditorPasswordReset(response.data);

      return response;
    });
}

export function deactivateAuditor(
  auditorId: number | string,
  config?: MmRequestConfig,
): AxiosPromise<MmAuditor> {
  return mmHttp
    .post(
      AUDITORS_PATH + '/' + String(auditorId) + '/deactivate',
      buildDeactivateAuditorBody(),
      Object.assign({ mmSection: 'auditors/deactivate' }, config),
    )
    .then((response: AxiosResponse) => {
      response.data = readAuditor(response.data);

      return response;
    });
}

/** undefined вторым аргументом обязателен: иначе настройки запроса уйдут его телом */
export function activateAuditor(
  auditorId: number | string,
  config?: MmRequestConfig,
): AxiosPromise<MmAuditor> {
  return mmHttp
    .post(
      AUDITORS_PATH + '/' + String(auditorId) + '/activate',
      undefined,
      Object.assign({ mmSection: 'auditors/activate' }, config),
    )
    .then((response: AxiosResponse) => {
      response.data = readAuditor(response.data);

      return response;
    });
}
